import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  RefreshCw,
  ChefHat,
  Image as ImageIcon,
  Sparkles,
  Package,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useImageGeneration } from "../context/ImageGenerationContext";

const STATUS_CONFIG = {
  completed: { Icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-900/20", label: "OK" },
  failed:    { Icon: AlertTriangle, color: "text-red-500",     bg: "bg-red-50 dark:bg-red-900/20",       label: "Error" },
  running:   { Icon: Loader2,       color: "text-blue-500",    bg: "bg-blue-50 dark:bg-blue-900/20",     label: "En curso", spin: true },
  pending:   { Icon: Clock,         color: "text-amber-500",   bg: "bg-amber-50 dark:bg-amber-900/20",   label: "En cola" },
};

const KIND_META = {
  recipes:     { Icon: ChefHat,     label: "Recetas" },
  images:      { Icon: ImageIcon,   label: "Imágenes" },
  allergens:   { Icon: Sparkles,    label: "Alérgenos" },
  ingredients: { Icon: Package,     label: "Ingredientes" },
};

function TaskRow({ task }) {
  const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const { Icon, color, bg, label, spin } = cfg;
  return (
    <div
      className={`flex items-start gap-3 p-2.5 rounded-lg ${bg} mb-1.5`}
      data-testid={`task-row-${task.task_id}`}
    >
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${color} ${spin ? "animate-spin" : ""}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {task.dish_name || task.task_id}
        </p>
        {task.error && (
          <p className="text-[11px] text-red-700 dark:text-red-400 mt-0.5 leading-snug break-words">
            {task.error}
          </p>
        )}
      </div>
      <Badge
        variant="outline"
        className={`text-[10px] flex-shrink-0 ${color} border-current`}
      >
        {label}
      </Badge>
    </div>
  );
}

export default function BatchDetailModal({ open, onClose, batchId, kind, restaurantId }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const { registerBatch } = useImageGeneration();

  const kindMeta = KIND_META[kind] || KIND_META.recipes;

  const fetchTasks = useCallback(async () => {
    if (!batchId || !restaurantId) return;
    setLoading(true);
    try {
      const data = await api.request(`/restaurants/${restaurantId}/ai/batch-tasks/${batchId}`);
      setTasks(data.tasks || []);
    } catch (e) {
      toast.error("No se pudo cargar el detalle del batch");
    } finally {
      setLoading(false);
    }
  }, [batchId, restaurantId]);

  useEffect(() => {
    if (open) { fetchTasks(); setActiveFilter("all"); }
  }, [open, fetchTasks]);

  const failedTasks = tasks.filter(t => t.status === "failed");
  const doneTasks   = tasks.filter(t => t.status === "completed");
  const pendingTasks = tasks.filter(t => t.status === "pending" || t.status === "running");

  const handleRetry = async () => {
    if (failedTasks.length === 0) return;
    setRetrying(true);
    try {
      const result = await api.request(
        `/restaurants/${restaurantId}/ai/batch-tasks/${batchId}/retry-failed`,
        { method: "POST" }
      );
      const newBatchId = result.batch_id;
      if (newBatchId) {
        registerBatch(kind, {
          batch_id: newBatchId,
          total: result.queued,
          label: `${kindMeta.label} (reintento)`,
        });
      }
      toast.success(result.message || `${result.queued} tarea(s) puesta(s) en cola`);
      onClose();
    } catch (e) {
      toast.error("Error al reintentar: " + (e.message || ""));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="batch-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <kindMeta.Icon className="w-4 h-4 text-primary" />
            Detalle por plato — {kindMeta.label}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Estado de cada plato en el batch de generación IA. Puedes ver errores y reintentar los fallidos.
          </DialogDescription>
        </DialogHeader>

        {/* Summary pills — clickable to filter */}
        <div className="flex gap-2 flex-wrap mb-1">
          <button
            onClick={() => setActiveFilter("all")}
            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors
              ${activeFilter === "all"
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-muted text-muted-foreground border-transparent hover:border-border"}`}
            data-testid="filter-all"
          >
            Todos ({tasks.length})
          </button>
          {failedTasks.length > 0 && (
            <button
              onClick={() => setActiveFilter("failed")}
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors
                ${activeFilter === "failed"
                  ? "bg-red-500 text-white border-red-500"
                  : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-transparent hover:border-red-300"}`}
              data-testid="filter-failed"
            >
              <AlertTriangle className="w-3 h-3" />
              {failedTasks.length} con error
            </button>
          )}
          {pendingTasks.length > 0 && (
            <button
              onClick={() => setActiveFilter("pending")}
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors
                ${activeFilter === "pending"
                  ? "bg-amber-500 text-white border-amber-500"
                  : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-transparent hover:border-amber-300"}`}
              data-testid="filter-pending"
            >
              <Clock className="w-3 h-3" />
              {pendingTasks.length} pendiente{pendingTasks.length > 1 ? "s" : ""}
            </button>
          )}
          {doneTasks.length > 0 && (
            <button
              onClick={() => setActiveFilter("completed")}
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors
                ${activeFilter === "completed"
                  ? "bg-emerald-500 text-white border-emerald-500"
                  : "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-transparent hover:border-emerald-300"}`}
              data-testid="filter-completed"
            >
              <CheckCircle2 className="w-3 h-3" />
              {doneTasks.length} completado{doneTasks.length > 1 ? "s" : ""}
            </button>
          )}
        </div>

        {/* Task list */}
        {loading ? (
          <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Cargando…</span>
          </div>
        ) : tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Sin tareas disponibles para este batch.
          </p>
        ) : (
          <ScrollArea className="h-72 pr-2">
            {tasks
              .filter(t => {
                if (activeFilter === "all") return true;
                if (activeFilter === "pending") return t.status === "pending" || t.status === "running";
                return t.status === activeFilter;
              })
              .map(t => <TaskRow key={t.task_id} task={t} />)}
            {tasks.filter(t => {
              if (activeFilter === "all") return true;
              if (activeFilter === "pending") return t.status === "pending" || t.status === "running";
              return t.status === activeFilter;
            }).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                Sin tareas con este estado.
              </p>
            )}
          </ScrollArea>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-1 border-t gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchTasks}
            disabled={loading}
            data-testid="batch-detail-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cerrar
            </Button>
            {failedTasks.length > 0 && (
              <Button
                size="sm"
                onClick={handleRetry}
                disabled={retrying}
                data-testid="batch-detail-retry-btn"
              >
                {retrying
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                Reintentar {failedTasks.length} fallido{failedTasks.length > 1 ? "s" : ""}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
